import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import orderApi, { Order } from "../services/orderApi";
import { formatCurrency } from "../utils/format";
import html2canvas from "html2canvas";
import config from "../config";

const statusColors: Record<string, string> = {
  pending: "#f59e0b",
  paid: "#18c601",
  COD: "#2563eb",
  processing: "#8b5cf6",
  shipped: "#6366f1",
  delivered: "#6366f1",
  completed: "#6366f1",
  cancelled: "#ef4444",
  failed: "#dc2626",
  refunded: "#6b7280",
};

const statusOrder = ["paid", "processing", "shipped", "delivered", "completed"];

const statusLabels: Record<string, string> = {
  pending: "Pending",
  paid: "Paid",
  COD: "Cash on Delivery",
  processing: "Processing Order",
  shipped: "Shipped",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
  failed: "Payment Failed",
  refunded: "Refunded",
};

function OrderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get("id") || (searchParams.get("orderId") ? searchParams.get("orderId") : null);
  const paymentStatus = searchParams.get("status") || "success";
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const orderCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError("No order ID provided");
        setLoading(false);
        return;
      }

      try {
        const response = await orderApi.getOrder(orderId);
        if (response.success && response.order) {
          setOrder(response.order);
        } else {
          setError("Order not found");
        }
      } catch (err) {
        setError("Failed to fetch order details");
      }
      setLoading(false);
    };

    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (orderId && paymentStatus === "success" && !order) {
      const timer = setTimeout(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("status");
        setSearchParams(newParams, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [order, orderId, paymentStatus, searchParams, setSearchParams]);

  if (loading) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #6366f1", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }}></div>
          <p style={{ color: "#374151" }}>Loading order...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !orderId) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "400px" }}>
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: "24px", color: "#dc2626" }}></i>
          </div>
          <h2 style={{ color: "#1a1a1a", marginBottom: "8px" }}>Error</h2>
          <p style={{ color: "#374151", marginBottom: "24px" }}>{error || "Invalid order"}</p>
          <Link to="/" style={{ color: "#6366f1", textDecoration: "none" }}>Back to Home</Link>
        </div>
      </div>
    );
  }

  if (paymentStatus === "failed") {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "400px" }}>
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="fas fa-times" style={{ fontSize: "24px", color: "#dc2626" }}></i>
          </div>
          <h2 style={{ color: "#1a1a1a", marginBottom: "8px" }}>Payment Failed</h2>
          <p style={{ color: "#374151", marginBottom: "24px" }}>Your payment was not completed. Please try again.</p>
          <Link to="/" style={{ color: "#6366f1", textDecoration: "none" }}>Back to Home</Link>
        </div>
      </div>
    );
  }

  if (paymentStatus === "cancelled") {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "400px" }}>
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="fas fa-ban" style={{ fontSize: "24px", color: "#d97706" }}></i>
          </div>
          <h2 style={{ color: "#1a1a1a", marginBottom: "8px" }}>Payment Cancelled</h2>
          <p style={{ color: "#374151", marginBottom: "24px" }}>Your payment was cancelled.</p>
          <Link to="/" style={{ color: "#6366f1", textDecoration: "none" }}>Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const currentStatusIndex = statusOrder.indexOf(order.orderStatus || "");
  const isCancelled = order.orderStatus === "cancelled";
  const trackingUrl = `${window.location.origin}/track?id=${orderId}`;

  const handleDownloadScreenshot = async () => {
    const element = orderCardRef.current;
    if (!element) return;

    setIsCapturing(true);
    // Small delay to ensure DOM is updated with display:none
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const canvas = await html2canvas(element, {
        allowTaint: true,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `Order-${orderId}-Receipt.png`;
      link.click();
    } catch (err) {
      console.error("Screenshot failed:", err);
      // Fallback: Show copy success message
      alert("Screenshot functionality requires html2canvas library. You can use your browser's print-to-PDF feature instead.");
    } finally {
      setIsCapturing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(trackingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: "fab fa-whatsapp",
      url: `https://wa.me/?text=Check my order: ${trackingUrl}`,
      color: "#25d366",
    },
    {
      name: "Facebook",
      icon: "fab fa-facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trackingUrl)}`,
      color: "#1877f2",
    },
    {
      name: "Twitter",
      icon: "fab fa-twitter",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(trackingUrl)}&text=Tracking my order`,
      color: "#1da1f2",
    },
    {
      name: "Copy Link",
      icon: "fas fa-link",
      url: "#",
      color: "#6366f1",
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        handleCopyLink();
      },
    },
  ];

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px" }}>
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        {/* Success Banner for new orders */}
        {paymentStatus === "success" && (
          <div style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", borderRadius: "16px", padding: "24px", marginBottom: "24px", color: "white", textAlign: "center" }}>
            <i className="fas fa-check-circle" style={{ fontSize: "32px", marginBottom: "8px" }}></i>
            <h2 style={{ margin: "0 0 8px", fontSize: "1.5rem" }}>Order Confirmed!</h2>
            <p style={{ margin: 0, opacity: 0.9 }}>Thank you for your purchase!</p>
          </div>
        )}

        {/* Capturable Content Wrapper */}
        <div ref={orderCardRef}>
        {/* Download & Share Section */}
        <div style={{ background: "white", borderRadius: "16px", padding: "24px", marginBottom: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: isCapturing ? "none" : "block" }}>
          <h3 style={{ fontWeight: "700", marginBottom: "16px", color: "#1a1a1a" }}>Save Your Receipt</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px" }}>
            {/* Download Screenshot */}
            <button
              onClick={handleDownloadScreenshot}
              aria-label="Download Screenshot"
              style={{
                background: "#6366f1",
                color: "white",
                border: "none",
                padding: "12px 16px",
                borderRadius: "8px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontSize: "0.9rem",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#4f46e5")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#6366f1")}
            >
              <i className="fas fa-download"></i>
              Screenshot
            </button>

            {/* Print Button */}
            <button
              onClick={() => window.print()}
              aria-label="Print Receipt"
              style={{
                background: "#8b5cf6",
                color: "white",
                border: "none",
                padding: "12px 16px",
                borderRadius: "8px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontSize: "0.9rem",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#7c3aed")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#8b5cf6")}
            >
              <i className="fas fa-print"></i>
              Print
            </button>

            {/* Share Section */}
            <div style={{ gridColumn: "1 / -1" }}>
              <p style={{ fontSize: "0.9rem", color: "#374151", marginBottom: "12px", marginTop: "12px" }}>Share tracking link:</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: "8px" }}>
                {shareOptions.map((option) => (
                  <a
                    key={option.name}
                    aria-label={option.name}
                    href={option.url}
                    onClick={option.onClick}
                    target={option.onClick ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    style={{
                      background: option.color,
                      color: "white",
                      border: "none",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                      textDecoration: "none",
                      opacity: 0.9,
                      transition: "opacity 0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = "0.9")}
                    title={copied && option.name === "Copy Link" ? "Copied!" : option.name}
                  >
                    <i className={option.icon}></i>
                    {copied && option.name === "Copy Link" ? "Copied!" : option.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ background: "white", borderRadius: "16px", padding: "32px", marginBottom: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <p style={{ color: "#374151", fontSize: "0.85rem", marginBottom: "4px" }}>Order Number</p>
              <h1 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a1a1a", margin: 0 }}>{order.orderId}</h1>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <span style={{ padding: "8px 16px", borderRadius: "20px", background: `${statusColors[order.paymentStatus || "pending"]}20`, color: statusColors[order.paymentStatus || "pending"], fontWeight: "600", fontSize: "0.85rem" }}>
                {statusLabels[order.paymentStatus || "pending"]}
              </span>
              <span style={{ padding: "8px 16px", borderRadius: "20px", background: `${statusColors[order.orderStatus || "pending"]}20`, color: statusColors[order.orderStatus || "pending"], fontWeight: "600", fontSize: "0.85rem" }}>
                {statusLabels[order.orderStatus || "pending"]}
              </span>
            </div>
          </div>

          {!isCancelled && order.orderStatus !== "failed" && (
            <div style={{ display: "flex", alignItems: "center", marginBottom: "32px" }}>
              {statusOrder.map((status, index) => (
                <div key={status} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <div style={{ 
                    width: "32px", 
                    height: "32px", 
                    borderRadius: "50%", 
                    background: index <= currentStatusIndex ? statusColors[status] : "#e5e7eb",
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    color: index <= currentStatusIndex ? "white" : "#9ca3af",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}>
                    {index < currentStatusIndex ? <i className="fas fa-check"></i> : index + 1}
                  </div>
                  {index < statusOrder.length - 1 && (
                    <div style={{ flex: 1, height: "3px", background: index < currentStatusIndex ? statusColors[status] : "#e5e7eb", margin: "0 8px" }}></div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <div style={{ background: "#f9fafb", padding: "16px", borderRadius: "12px" }}>
              <p style={{ color: "#374151", fontSize: "0.85rem", marginBottom: "4px" }}>Customer</p>
              <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0 }}>{order.customerName}</p>
            </div>
            <div style={{ background: "#f9fafb", padding: "16px", borderRadius: "12px" }}>
              <p style={{ color: "#374151", fontSize: "0.85rem", marginBottom: "4px" }}>Email</p>
              <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0 }}>{order.email}</p>
            </div>
            <div style={{ background: "#f9fafb", padding: "16px", borderRadius: "12px" }}>
              <p style={{ color: "#374151", fontSize: "0.85rem", marginBottom: "4px" }}>Total</p>
               <p style={{ fontWeight: "700", color: "#6366f1", margin: 0, fontSize: "1.25rem" }}>{formatCurrency(order.totalAmount)}</p>
            </div>
          </div>
        </div>

        <div style={{ background: "white", borderRadius: "16px", padding: "32px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <h3 style={{ fontWeight: "700", marginBottom: "20px", color: "#1a1a1a" }}>Order Items</h3>
          <div style={{ display: "grid", gap: "16px" }}>
            {order.items?.map((item, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px", background: "#f9fafb", borderRadius: "12px" }}>
                <div style={{ width: "60px", height: "60px", borderRadius: "8px", background: "white", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {(item as any).imageUrl ? (
                    <img src={(item as any).imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <i className="fas fa-box" style={{ color: "#9ca3af" }}></i>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0 }}>{item.name}</p>
                   <p style={{ color: "#374151", fontSize: "0.85rem", margin: "4px 0 0" }}>Qty: {item.quantity} × {formatCurrency(item.unitPrice)}</p>
                </div>
                 <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0 }}>{formatCurrency(item.totalPrice)}</p>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "24px", paddingTop: "24px" }}>
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ fontWeight: "700", marginBottom: "16px", color: "#1a1a1a" }}>Order Summary</h3>
              <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px", border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #e5e7eb" }}>
                  <span style={{ color: "#374151" }}>Subtotal (Products)</span>
                   <span style={{ fontWeight: "600", color: "#1a1a1a" }}>{formatCurrency(Number(order.totalAmount) - Number(order.shippingFee || 0))}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #e5e7eb" }}>
                  <span style={{ color: "#374151" }}>Shipping Fee</span>
                   <span style={{ fontWeight: "600", color: "#8b5cf6" }}>{formatCurrency(order.shippingFee || 0)}</span>
                </div>
                 <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0" }}>
                   <span style={{ fontWeight: "700", color: "#1a1a1a", fontSize: "1.1rem" }}>Total</span>
                   <span style={{ fontWeight: "700", color: "#6366f1", fontSize: "1.1rem" }}>{formatCurrency(order.totalAmount)}</span>
                 </div>
              </div>
            </div>

            <div>
              <h3 style={{ fontWeight: "700", marginBottom: "16px", color: "#1a1a1a" }}>Shipping Information</h3>
              <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "20px" }}>
                <div style={{ display: "grid", gap: "16px" }}>
                  <div>
                    <p style={{ fontSize: "0.85rem", color: "#374151", marginBottom: "6px" }}>Recipient Name</p>
                    <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0 }}>{order.customerName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "0.85rem", color: "#374151", marginBottom: "6px" }}>Address</p>
                    <p style={{ fontWeight: "500", color: "#1a1a1a", margin: 0 }}>{order.address}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "0.85rem", color: "#374151", marginBottom: "6px" }}>City & Province</p>
                    <p style={{ fontWeight: "500", color: "#1a1a1a", margin: 0 }}>{order.city}{order.province ? `, ${order.province}` : ""}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: "0.85rem", color: "#374151", marginBottom: "6px" }}>Contact Number</p>
                    <p style={{ fontWeight: "500", color: "#1a1a1a", margin: 0 }}>{order.phone || "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "24px" }}>
              <h3 style={{ fontWeight: "700", marginBottom: "16px", color: "#1a1a1a" }}>Need Help?</h3>
              <div style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", borderRadius: "12px", padding: "24px", color: "white" }}>
                <p style={{ fontSize: "0.95rem", marginBottom: "16px", margin: 0 }}>Have questions about your order? We're here to help!</p>
                <div style={{ display: "grid", gap: "12px", marginTop: "16px" }}>
                  {config.contact.email && (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <i className="fas fa-envelope" style={{ fontSize: "18px" }}></i>
                      <div>
                        <p style={{ fontSize: "0.85rem", opacity: 0.9, margin: 0 }}>Email Support</p>
                        <a href={`mailto:${config.contact.email}`} style={{ color: "white", fontWeight: "600", textDecoration: "none" }}>{config.contact.email}</a>
                      </div>
                    </div>
                  )}
                  {config.contact.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <i className="fas fa-phone" style={{ fontSize: "18px" }}></i>
                      <div>
                        <p style={{ fontSize: "0.85rem", opacity: 0.9, margin: 0 }}>Phone Support</p>
                        <a href={`tel:${config.contact.phone}`} style={{ color: "white", fontWeight: "600", textDecoration: "none" }}>{config.contact.phone}</a>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid rgba(255, 255, 255, 0.2)" }}>
                  <span style={{ color: "white", fontSize: "0.85rem", opacity: 0.9 }}>Terms & Conditions</span>
                  <span style={{ color: "white", fontSize: "0.85rem", margin: "0 8px", opacity: 0.5 }}> • </span>
                  <span style={{ color: "white", fontSize: "0.85rem", opacity: 0.9 }}>Privacy Policy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div> {/* Close Capturable Content Wrapper */}
      </div>
    </div>
  );
}

export default OrderPage;
